import type { FC } from 'react';
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { GridPagination, SuspenseBoundary } from 'components/common';
import { useAdsHistoryPacks } from 'features/cabinet/facebook/api';
import AdsHistoryGroupedFilters from 'features/cabinet/facebook/components/AdsHistoryGroupedFilters';
import type { AdsHistoryGroupMode } from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import AdsHistoryGroupedView from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import type { AdsHistoryGroupedEntry } from 'features/cabinet/facebook/components/AdsHistoryGroupedView.mockData';
import type { AdsHistoryPacksResponse } from 'features/cabinet/facebook/api/getAdsHistoryPacks';
import { applyGroupedFilters, EMPTY_FILTERS, type AdsHistoryGroupedFilterValues } from 'features/cabinet/facebook/utils/adsHistoryGroupedFilters';
import { usePagination } from 'hooks/usePagination';

const normalizeEntries = (data: AdsHistoryPacksResponse): AdsHistoryGroupedEntry[] =>
  data.data.flatMap((week) =>
    week.packs.flatMap((pack) =>
      pack.ads.map((ad) => ({
        ...ad,
        pack_name: pack.pack_name,
        hypothesis: '',
        email: pack.representative.created_by ?? '',
        created_at: pack.representative.created_at ?? '',
        ad_account: pack.representative.ad_account,
        ad_set: pack.representative.adset,
        message: ad.message ?? pack.representative.message ?? '',
        status: ad.status ?? pack.representative.status,
        ad_id: ad.ad_id ?? '',
      })),
    ),
  );

const ScalingHistoryByPacksFilters: FC<{
  filters: AdsHistoryGroupedFilterValues;
  onFilterChange: (next: AdsHistoryGroupedFilterValues) => void;
  pagination: ReturnType<typeof usePagination>;
}> = ({ filters, onFilterChange, pagination }) => {
  const { data } = useAdsHistoryPacks({
    params: {
      marketing_process: 'scaling',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const entries = useMemo(() => normalizeEntries(data), [data]);

  return (
    <AdsHistoryGroupedFilters
      entries={entries}
      filters={filters}
      onFilterChange={onFilterChange}
    />
  );
};

const ScalingHistoryByPacksGrid: FC<{
  mode: AdsHistoryGroupMode;
  filters: AdsHistoryGroupedFilterValues;
  pagination: ReturnType<typeof usePagination>;
}> = ({ mode, filters, pagination }) => {
  const { data } = useAdsHistoryPacks({
    params: {
      marketing_process: 'scaling',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const entries = useMemo(() => normalizeEntries(data), [data]);
  const filteredEntries = useMemo(() => applyGroupedFilters(entries, filters), [entries, filters]);

  return (
    <>
      <AdsHistoryGroupedView
        entries={filteredEntries}
        mode={mode}
        emptyPlaceholder="No scaling ads by packs posted recently."
      />
      <GridPagination
        count={data.count}
        isLoading={false}
        page={pagination.page}
        perPage={pagination.perPage}
        handlePageChange={pagination.handlePageChange}
        handleRowsPerPageChange={pagination.handleRowsPerPageChange}
      />
    </>
  );
};

const ScalingHistoryByPacksPage: FC = () => {
  const pagination = usePagination([], 50);
  const [filters, setFilters] = useState<AdsHistoryGroupedFilterValues>(EMPTY_FILTERS);

  return (
    <Box sx={{ p: 0 }}>
      <SuspenseBoundary>
        <ScalingHistoryByPacksFilters
          filters={filters}
          onFilterChange={setFilters}
          pagination={pagination}
        />
      </SuspenseBoundary>
      <SuspenseBoundary>
        <ScalingHistoryByPacksGrid
          mode="packs"
          filters={filters}
          pagination={pagination}
        />
      </SuspenseBoundary>
    </Box>
  );
};

export default ScalingHistoryByPacksPage;
